<script setup lang="ts">
import { useDialogPluginComponent } from 'quasar';
import { useI18n } from 'vue-i18n';

import StandardInput from '@/components/StandardInput.vue';
import { Constants } from '@/constants';
import type { FeedbackEntryDto, Locales } from '@/services/types/api.types';
import { useFeedbackEntriesStore } from '@/stores/feedbackEntries.store';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { getFullName } from '@/helpers/text.helper';

// PROPS
const props = defineProps<{
  feedbackEntry: FeedbackEntryDto;
  languageEnabled: Locales[];
}>();

// CONSTS
const { t } = useI18n();
const feedbackEntriesStore = useFeedbackEntriesStore();

// EMITS
defineEmits([...useDialogPluginComponent.emits]);

// CONST
const { dialogRef, onDialogCancel, onDialogHide, onDialogOK } = useDialogPluginComponent();

// FUNCTIONS
const getStatusLabel = (status: number) => {
  switch (status) {
    case Constants.ProjectModule.FeedbackEntry.Status.Pending:
      return t('project-module.feedback.status.pending');
    case Constants.ProjectModule.FeedbackEntry.Status.Approved:
      return t('project-module.feedback.status.approved');
    case Constants.ProjectModule.FeedbackEntry.Status.Refused:
      return t('project-module.feedback.status.refused');
    default:
      return '';
  }
};

const onApprove = async () => {
  await wrapSavingOperation(
    async () => {
      await feedbackEntriesStore.approveFeedbackEntry(props.feedbackEntry);
      return true;
    },
    () => onDialogOK(),
  );
};

const onRefuse = async () => {
  await wrapSavingOperation(
    async () => {
      await feedbackEntriesStore.refuseFeedbackEntry(props.feedbackEntry);
      return true;
    },
    () => onDialogOK(),
  );
};
</script>

<template>
  <q-dialog ref="dialogRef" @hide="onDialogHide" persistent>
    <q-card class="q-dialog-plugin" style="width: 600px">
      <q-card-section class="row">
        <div class="text-h6">{{ t('project-module.feedback.dialogs.title') }}</div>
        <q-space />
      </q-card-section>
      <q-separator />
      <q-card-section>
        <standard-input
          name="name"
          :label="t('global.label.name')"
          type="text"
          :model-value="getFullName(props.feedbackEntry.user)"
          readonly
          outlined
        />
        <standard-input
          name="feedback"
          :label="t('global.label.message')"
          type="textarea"
          :model-value="[props.feedbackEntry.message]"
          readonly
          outlined
        />
        <standard-input
          name="status"
          :label="t('global.label.status')"
          type="text"
          :model-value="[getStatusLabel(props.feedbackEntry.status)]"
          readonly
          outlined
        />
      </q-card-section>
      <q-card-section class="row">
        <q-card-actions v-if="props.feedbackEntry.status === Constants.ProjectModule.FeedbackEntry.Status.Pending">
          <q-btn color="primary" :label="t('project-module.feedback.dialogs.buttons.approve')" @click="onApprove" />
          <q-btn color="primary" :label="t('project-module.feedback.dialogs.buttons.refuse')" @click="onRefuse" />
        </q-card-actions>
        <q-space />
        <q-card-actions>
          <q-btn color="primary" flat :label="t('global.close')" @click="onDialogCancel" />
        </q-card-actions>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>
